"use client";

import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { formatAddress, Separator, useToasts } from "@opensea/ui-kit";
import { SwapHoriz } from "@opensea/ui-kit/icons";
import { isAddressEqual, normalizeAddress, TTL } from "@opensea/utils";
import { useTranslations } from "next-intl";
import { useRenderUsdBalance } from "@/components/settings/wallet-linking/use-render-usd-balance";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { useConnectFlow } from "@/hooks/use-connect-flow";
import { useAddress, useWalletConnectionResolver } from "@/hooks/wallet";
import { AccountSwitcherAccountItemsFragment } from "./AccountSwitcherAccountItems.fragment";
import { AccountSwitcherAccountItemsSkeleton } from "./AccountSwitcherAccountItemsSkeleton";
import { AccountSwitcherTopAccount } from "./AccountSwitcherTopAccount";
import { LinkedAccountSwitcherItem } from "./LinkedAccountSwitcherItem";
const OPERATION_CONTEXT = {
  suspense: false,
  ttl: TTL["2s"]
};
const query = graphql(`
    query AccountSwitcherAccountItemsNew($address: Address!) {
      profilesByAccount(address: $address) {
        account {
          ... on Profile {
            ...AccountSwitcherAccountItems
          }
        }
        profiles {
          ... on Profile {
            ...AccountSwitcherAccountItems
          }
        }
      }
      walletsByAccount {
        items {
          address
          lastSeenConnectorId
        }
      }
    }
  `, [AccountSwitcherAccountItemsFragment]);
function AccountSwitcherAccountItemsBase({
  connections,
  onSelect
}) {
  const t = useTranslations("AccountSwitcherAccountItems");
  const activeAddress = useAddress();
  const {
    activeConnector,
    connectionsByAddress,
    previouslyConnectedAccountsByAddress,
    resolveConnector
  } = useWalletConnectionResolver({
    connections
  });
  const renderUsdBalance = useRenderUsdBalance();
  const {
    addToast
  } = useToasts();
  const connectFlow = useConnectFlow({
    source: "Account Switcher Account Items"
  });

  // Get profiles by account using the current active address
  const [{
    data,
    fetching
  }] = useQuery({
    query,
    variables: {
      address: activeAddress || ""
    },
    context: OPERATION_CONTEXT,
    pause: !activeAddress // Only execute query when we have an active address
  }, {
    throwOnError: false
  });

  // Get top account first
  let topAccount;
  const topRaw = data?.profilesByAccount?.account;
  if (topRaw && topRaw.__typename === "Profile") {
    topAccount = readFragment(AccountSwitcherAccountItemsFragment, topRaw);
  }

  // Filter profiles and use proper fragment type with sorting
  const profilesByAccount = (data?.profilesByAccount?.profiles ?? []).filter(profile => profile?.__typename === "Profile").map(profileData => readFragment(AccountSwitcherAccountItemsFragment, profileData)).sort((a, b) => {
    // 1. Primary wallet comes first
    if (topAccount) {
      const topAccountData = readFragment(AccountSwitcherAccountItemsFragment, topAccount);
      const aIsPrimary = isAddressEqual(a.address, topAccountData.address);
      const bIsPrimary = isAddressEqual(b.address, topAccountData.address);
      if (aIsPrimary && !bIsPrimary) {
        return -1;
      }
      if (!aIsPrimary && bIsPrimary) {
        return 1;
      }
    }

    // 2. Sort by estimated USD value (descending). Unknown values last.
    const aUsd = a.portfolioSummary?.estimatedTokenValue?.usd ?? -1;
    const bUsd = b.portfolioSummary?.estimatedTokenValue?.usd ?? -1;
    if (aUsd !== bUsd) {
      return bUsd - aUsd;
    }

    // 3. Alphabetical tie-breaker using display name or formatted address
    const aName = a.displayName || formatAddress(normalizeAddress(a.address));
    const bName = b.displayName || formatAddress(normalizeAddress(b.address));
    return aName.localeCompare(bName);
  });

  // Create a map of wallet connector IDs by address
  const walletConnectorIdsByAddress = new Map((data?.walletsByAccount?.items ?? []).map(wallet => [normalizeAddress(wallet.address), wallet.lastSeenConnectorId]));
  if (fetching && !data) {
    return <AccountSwitcherAccountItemsSkeleton />;
  }
  return <>
      {topAccount ? <>
          <AccountSwitcherTopAccount account={topAccount} isLink usdTotalValue={profilesByAccount.reduce((sum, p) => sum + (p.portfolioSummary?.estimatedTokenValue?.usd ?? 0), 0)} variant="compact" walletCount={profilesByAccount.length} />
          <Separator />
        </> : null}

      {profilesByAccount.map(profile_0 => {
      const normalizedAddress = normalizeAddress(profile_0.address);
      const connection = connectionsByAddress.get(normalizedAddress);
      const lastSeenConnectorId = walletConnectorIdsByAddress.get(normalizedAddress);
      const previouslyConnectedAccount = previouslyConnectedAccountsByAddress[normalizedAddress];
      const resolvedConnector = resolveConnector(profile_0.address, {
        lastSeenConnectorId
      });
      const connectorId = lastSeenConnectorId || resolvedConnector?.id || previouslyConnectedAccount?.connectorId;
      const isActive = connection?.connector.id === activeConnector?.id && isAddressEqual(profile_0.address, activeAddress || "");
      let isPrimary = false;
      if (topAccount) {
        const topAccountData_0 = readFragment(AccountSwitcherAccountItemsFragment, topAccount);
        isPrimary = isAddressEqual(profile_0.address, topAccountData_0.address);
      }
      const handleClick = isActive ? undefined : () => {
        if (connection) {
          onSelect(connection.connector, profile_0.address);
          return;
        }
        if (resolvedConnector) {
          onSelect(resolvedConnector, profile_0.address);
          return;
        }
        addToast({
          title: t("walletNotConnected"),
          icon: <SwapHoriz />,
          duration: 5000,
          dismissible: true
        });
        connectFlow();
      };
      return <LinkedAccountSwitcherItem address={profile_0.address} avatarSize={20} className="pl-4" connectorId={connectorId ?? undefined} description={renderUsdBalance(profile_0.portfolioSummary?.estimatedTokenValue?.usd ?? undefined)} displayName={profile_0.displayName} imageUrl={profile_0.imageUrl} isLink={isActive} isPrimary={isPrimary} key={normalizedAddress} onClick={handleClick} showCheck variant="compact" />;
    })}
    </>;
}
export const AccountSwitcherAccountItems = withSuspense(AccountSwitcherAccountItemsBase, {
  fallback: <AccountSwitcherAccountItemsSkeleton />
});